import { Link } from 'react-router-dom'
import { ContentCard } from '../components/ContentCard'
import { SearchBox } from '../components/SearchBox'
import { SubscribeCTA } from '../components/SubscribeCTA'
import { contentHref, getContentById, getLatestLongform } from '../data/content'
import { DT_EPISODES, DT_SERIES } from '../data/deiveegamTheriyuma'
import { DEITIES } from '../data/deities'
import {
  EVERGREEN_CONTENT_IDS,
  HOME_CAMPAIGN,
  HOME_FEATURED_PDF_IDS,
  HOME_SEO,
  NAVRATRI_2026,
  formatYtDateTa,
  isCampaignLive,
  isNavratriSeasonActive,
  isWithinDays,
} from '../data/home'
import { SITE } from '../data/site'
import type { ContentItem } from '../data/types'
import { useSeo } from '../hooks/useSeo'
import './Home.css'

function thumb(videoId: string) {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
}

function HomeReleaseCard({
  item,
  seasonalBadge,
}: {
  item: ContentItem
  seasonalBadge?: boolean
}) {
  const videoId = item.youtubeVideoId?.trim()
  const date = item.youtubePublishedAt
  const badges: string[] = []
  if (date && isWithinDays(date, 30)) badges.push('புதியது')
  if (seasonalBadge) badges.push('நவராத்திரி சிறப்பு')
  else if (item.pdfUrl && badges.length < 2) badges.push('PDF')

  return (
    <Link to={contentHref(item)} className="home-release">
      <div className="home-release__media">
        {videoId ? (
          <img
            src={thumb(videoId)}
            alt={`${item.titleEn} — ${item.titleTa}`}
            width={480}
            height={270}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="home-release__placeholder" aria-hidden="true" />
        )}
        {badges.length > 0 && (
          <div className="home-release__badges">
            {badges.slice(0, 2).map((b) => (
              <span key={b} className="home-release__badge">
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="home-release__body">
        <h3 className="home-release__title">{item.cardTitleTa ?? item.titleTa}</h3>
        <p className="home-release__en">{item.titleEn}</p>
        {date && <p className="home-release__date">YouTube: {formatYtDateTa(date)}</p>}
      </div>
    </Link>
  )
}

function DtTeaserCard({
  title,
  videoId,
  anchor,
  tag,
}: {
  title: string
  videoId: string
  anchor: string
  tag: string
}) {
  return (
    <Link to={`/deiveegam-theriyuma#${anchor}`} className="home-release home-release--dt">
      <div className="home-release__media">
        <img
          src={thumb(videoId)}
          alt={title}
          width={480}
          height={270}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="home-release__body">
        <p className="home-release__tag">{tag}</p>
        <h3 className="home-release__title">{title}</h3>
      </div>
    </Link>
  )
}

export function HomePage() {
  useSeo({
    title: HOME_SEO.title,
    description: HOME_SEO.description,
    path: '/',
    skipTitleSuffix: true,
  })

  const navratriOn = isNavratriSeasonActive()
  const campaignOn = isCampaignLive()
  const latest = getLatestLongform(6)
  const dtLatest = DT_EPISODES.slice(0, 3)
  const navratriItems = NAVRATRI_2026.contentIds.map(getContentById).filter(Boolean) as ContentItem[]
  const evergreen = EVERGREEN_CONTENT_IDS.map(getContentById).filter(Boolean) as ContentItem[]
  const pdfItems = HOME_FEATURED_PDF_IDS.map(getContentById).filter(
    (c): c is ContentItem => Boolean(c?.pdfUrl),
  )

  return (
    <>
      {navratriOn ? (
        <section className="home-seasonal-hero" aria-labelledby="home-seasonal-title">
          <div className="home-seasonal-hero__bg" aria-hidden="true" />
          <div className="container home-seasonal-hero__inner">
            <p className="home-seasonal-hero__eyebrow">{NAVRATRI_2026.eyebrow}</p>
            <h1 id="home-seasonal-title" className="home-seasonal-hero__title">
              {NAVRATRI_2026.titleTa}
            </h1>
            <p className="home-seasonal-hero__support">{NAVRATRI_2026.supportTa}</p>
            <div className="btn-row home-seasonal-hero__actions">
              <a className="btn btn--gold" href={NAVRATRI_2026.primaryCtaHash}>
                {NAVRATRI_2026.primaryCtaLabel}
              </a>
              <a
                className="btn btn--ghost"
                href={SITE.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {NAVRATRI_2026.secondaryCtaLabel}
              </a>
              {campaignOn &&
                (HOME_CAMPAIGN.ctaUrl.startsWith('http') ? (
                  <a
                    className="btn btn--outline home-seasonal-hero__giveaway"
                    href={HOME_CAMPAIGN.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {HOME_CAMPAIGN.ctaLabel}
                  </a>
                ) : (
                  <Link className="btn btn--outline home-seasonal-hero__giveaway" to={HOME_CAMPAIGN.ctaUrl}>
                    {HOME_CAMPAIGN.ctaLabel}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="home-hero">
          <h1 className="sr-only">{SITE.nameTa} — அதிகாரப்பூர்வ இணையதளம்</h1>
          <div className="home-hero__art">
            <img
              src={SITE.heroDesktop}
              alt="தெய்வீக ஓசை | Deiveega Oosai — Chants, Devotional Songs & Talks"
              width={1920}
              height={502}
              decoding="async"
              fetchPriority="high"
            />
          </div>
          <div className="container home-hero__cta">
            <p>தமிழ் பக்திப் பாடல்கள் • ஸ்துதிகள் • ஆன்மீக அறிவு</p>
            <div className="btn-row">
              <a className="btn btn--gold" href={SITE.youtubeUrl} target="_blank" rel="noopener noreferrer">
                ▶ YouTube-ல் பாடல்களைக் கேளுங்கள்
              </a>
              <Link className="btn btn--ghost" to="/songs">
                பாடல்கள்
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="section section--tight">
        <div className="container">
          <SearchBox />
        </div>
      </section>

      <section className="section section--alt">
        <div className="container">
          <div className="section-header">
            <p className="eyebrow">தெய்வங்கள்</p>
            <h2>தெய்வம் வாரியாக உலாவுங்கள்</h2>
            <p>விநாயகர் முதல் ஐயப்பன் வரை — உங்கள் பக்திக்கு ஏற்ற உள்ளடக்கம்.</p>
          </div>
          <div className="deity-row">
            {DEITIES.filter((d) => d.id !== 'other').map((d) => (
              <Link key={d.id} to={`/deities/${d.id}`} className="deity-chip">
                <span className="deity-chip__ta">{d.nameTa}</span>
                <span className="deity-chip__en">{d.nameEn}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="latest-heading">
        <div className="container">
          <div className="section-header">
            <p className="eyebrow">புதியவை</p>
            <h2 id="latest-heading">புதிய வெளியீடுகள்</h2>
            <p>சமீபத்தில் வெளியான பாடல்கள், ஸ்துதிகள் மற்றும் நாமாவளிகள்</p>
          </div>
          <div className="home-release-grid">
            {latest.map((item) => (
              <HomeReleaseCard key={item.id} item={item} />
            ))}
          </div>
          <div className="btn-row" style={{ marginTop: '1.25rem' }}>
            <Link className="btn btn--outline" to="/songs">
              அனைத்து புதிய பதிவுகளும்
            </Link>
            <Link className="btn btn--outline" to="/stutis">
              ஸ்துதிகள்
            </Link>
          </div>
        </div>
      </section>

      {navratriOn && (
        <section className="section home-navratri" id="navratri-collection" aria-labelledby="navratri-heading">
          <div className="container">
            <div className="section-header">
              <p className="eyebrow">பண்டிகை</p>
              <h2 id="navratri-heading">நவராத்திரி 2026 சிறப்பு</h2>
              <p>துர்கை · லட்சுமி · சரஸ்வதி — நேரடி வலைத்தளப் பக்கங்கள் மட்டும்</p>
            </div>
            {campaignOn && (
              <aside className="home-campaign-card">
                <p className="eyebrow">{HOME_CAMPAIGN.label}</p>
                <h3>{HOME_CAMPAIGN.headline}</h3>
                <p>{HOME_CAMPAIGN.text}</p>
                {HOME_CAMPAIGN.ctaUrl.startsWith('http') ? (
                  <a
                    className="btn btn--gold"
                    href={HOME_CAMPAIGN.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {HOME_CAMPAIGN.ctaLabel}
                  </a>
                ) : (
                  <Link className="btn btn--gold" to={HOME_CAMPAIGN.ctaUrl}>
                    {HOME_CAMPAIGN.ctaLabel}
                  </Link>
                )}
              </aside>
            )}
            <div className="home-release-grid">
              {navratriItems.map((item) => (
                <HomeReleaseCard key={item.id} item={item} seasonalBadge />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section section--alt" aria-labelledby="dt-teaser-heading">
        <div className="container">
          <div className="section-header">
            <p className="eyebrow">குறுந்தகவல் தொடர்</p>
            <h2 id="dt-teaser-heading">{DT_SERIES.heroTitle}</h2>
            <p>
              நம் ஆன்மீக மரபுகளுக்குப் பின்னால் இருக்கும் சுவாரஸ்யமான அர்த்தங்களை ஒரு நிமிடத்தில்
              தெரிந்துகொள்ளுங்கள்.
            </p>
          </div>
          <div className="home-release-grid home-release-grid--3">
            {dtLatest.map((ep) => (
              <DtTeaserCard
                key={ep.id}
                title={ep.titleTa}
                videoId={ep.youtubeVideoId}
                anchor={ep.anchor}
                tag={ep.cardTag}
              />
            ))}
          </div>
          <div style={{ marginTop: '1.25rem' }}>
            <Link className="btn btn--primary" to="/deiveegam-theriyuma">
              அனைத்து 12 பகுதிகளையும் பாருங்கள்
            </Link>
          </div>
        </div>
      </section>

      <section className="section" aria-labelledby="pdf-heading">
        <div className="container">
          <div className="section-header">
            <p className="eyebrow">இலவசம்</p>
            <h2 id="pdf-heading">இலவச PDF பதிவிறக்கங்கள்</h2>
            <p>நவராத்திரிக்கு ஏற்ற ஸ்தோத்திர PDFகள் — உள்நுழைவு தேவையில்லை.</p>
          </div>
          <div className="home-pdf-grid">
            {pdfItems.map((item) => (
              <a
                key={item.id}
                className="home-pdf-card"
                href={item.pdfUrl}
                download
                onClick={() => {
                  const w = window as Window & { gtag?: (...args: unknown[]) => void }
                  w.gtag?.('event', 'pdf_download', { event_label: item.id, event_category: 'home' })
                }}
              >
                <span className="home-pdf-card__badge">PDF</span>
                <h3>{item.titleEn}</h3>
                <p>{item.cardTitleTa ?? item.titleTa}</p>
                <span className="home-pdf-card__cta">பதிவிறக்கு →</span>
              </a>
            ))}
          </div>
          <div style={{ marginTop: '1.25rem' }}>
            <Link className="btn btn--outline" to="/downloads">
              அனைத்து PDFகளும்
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--alt" aria-labelledby="evergreen-heading">
        <div className="container">
          <div className="section-header">
            <p className="eyebrow">எப்போதும்</p>
            <h2 id="evergreen-heading">எப்போதும் கேட்கப்படும்</h2>
            <p>பழைய உயர்மதிப்புப் பாடல்கள் — “புதியவை” அல்ல; நிலையான பக்தித் தேர்வுகள்.</p>
          </div>
          <div className="card-grid card-grid--3">
            {evergreen.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      </section>

      <SubscribeCTA />
    </>
  )
}
